import { create } from 'zustand';
import { Clip as Recording } from '@goldcast/player-config/src/types/clip-types';
import { getIsDuplicateRecording } from '@goldcast/player-config/src/utils/clipUtils';
import { loadClips as fetchRecordings } from '@/libs/clipContentUtil';
import { showErrorToast } from '@/libs/toast/toast';
import { retryAsync } from '@/libs/utils';

// A broadcast's recordings are the single original (full-length, non-duplicate) recording plus
// zero or more duplicates. Both are fetched together (the `includeDuplicates` request returns
// the full mix) and both need their publish status tracked, so they live side by side here
// instead of the original being sourced from elsewhere.
export interface RecordingDuplicatesEntry {
  original: Recording | null;
  duplicates: Recording[];
}

const EMPTY_ENTRY: RecordingDuplicatesEntry = { original: null, duplicates: [] };

interface RecordingDuplicatesState {
  recordings: Record<string, RecordingDuplicatesEntry>;
  isLoading: boolean;
  overwriteRecordings: (broadcastId: string, recordings: Recording[]) => void;
  appendRecording: (broadcastId: string, recording: Recording) => void;
  updateRecording: (broadcastId: string, updated: Recording) => void;
  removeRecording: (broadcastId: string, recordingId: string) => void;
  clearRecordings: (broadcastId?: string) => void;
  loadRecordings: (broadcastId: string) => Promise<void>;
}

// Splits a mixed list of clips (as returned by the `includeDuplicates` fetch) into the single
// original recording and its duplicates.
const partitionRecordings = (recordings: Recording[]): RecordingDuplicatesEntry => ({
  original:
    recordings.find(recording => !getIsDuplicateRecording(recording) && recording.asset_metadata.is_full_length) ??
    null,
  duplicates: recordings.filter(getIsDuplicateRecording)
});

export const useRecordingDuplicatesStore = create<RecordingDuplicatesState>()((set, get) => ({
  recordings: {},
  isLoading: false,

  overwriteRecordings: (broadcastId, recordings) =>
    set(state => ({
      recordings: { ...state.recordings, [broadcastId]: partitionRecordings(recordings) }
    })),

  appendRecording: (broadcastId, recording) =>
    set(state => {
      const entry = state.recordings[broadcastId] ?? EMPTY_ENTRY;
      return {
        recordings: {
          ...state.recordings,
          [broadcastId]: { ...entry, duplicates: [recording, ...entry.duplicates] }
        }
      };
    }),

  // Routes the update to whichever half of the entry the recording belongs to, so the same
  // helper can be used for both the original (e.g. on publish-status changes) and duplicates.
  updateRecording: (broadcastId, updated) =>
    set(state => {
      const entry = state.recordings[broadcastId] ?? EMPTY_ENTRY;
      if (entry.original?.id === updated.id) {
        return { recordings: { ...state.recordings, [broadcastId]: { ...entry, original: updated } } };
      }
      return {
        recordings: {
          ...state.recordings,
          [broadcastId]: {
            ...entry,
            duplicates: entry.duplicates.map(r => (r.id === updated.id ? updated : r))
          }
        }
      };
    }),

  // The original recording can never be removed this way; only duplicates support deletion.
  removeRecording: (broadcastId, recordingId) =>
    set(state => {
      const entry = state.recordings[broadcastId] ?? EMPTY_ENTRY;
      return {
        recordings: {
          ...state.recordings,
          [broadcastId]: { ...entry, duplicates: entry.duplicates.filter(r => r.id !== recordingId) }
        }
      };
    }),

  clearRecordings: broadcastId => {
    if (broadcastId) {
      set(state => ({ recordings: { ...state.recordings, [broadcastId]: { original: null, duplicates: [] } } }));
    } else {
      set({ recordings: {} });
    }
  },

  loadRecordings: async broadcastId => {
    const existing = get().recordings[broadcastId];
    if (existing?.original || (existing?.duplicates.length ?? 0) > 0) {
      return;
    }

    set({ isLoading: true });
    try {
      // No `clipTypes` filter: duplicate recordings are FULL_RECORDING assets and must stay in the results.
      // This also fetches the original recording alongside its duplicates so both live in the store.
      const allClipsAndDuplicates = await retryAsync(() => fetchRecordings(broadcastId, { includeDuplicates: true }));
      get().overwriteRecordings(broadcastId, allClipsAndDuplicates);
      const onlyDuplicates = allClipsAndDuplicates.filter(getIsDuplicateRecording);
      get().overwriteRecordings(broadcastId, onlyDuplicates);
    } catch {
      get().clearRecordings(broadcastId);
      showErrorToast('Failed to load duplicate recordings. Please try again.', {
        toastId: 'load-duplicate-recordings-error'
      });
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Standalone helpers for non-React callers
export const overwriteRecordingDuplicates = (broadcastId: string, recordings: Recording[]) =>
  useRecordingDuplicatesStore.getState().overwriteRecordings(broadcastId, recordings);

export const appendRecordingDuplicate = (broadcastId: string, recording: Recording) =>
  useRecordingDuplicatesStore.getState().appendRecording(broadcastId, recording);

export const updateRecordingDuplicate = (broadcastId: string, updated: Recording) =>
  useRecordingDuplicatesStore.getState().updateRecording(broadcastId, updated);

export const removeRecordingDuplicate = (broadcastId: string, recordingId: string) =>
  useRecordingDuplicatesStore.getState().removeRecording(broadcastId, recordingId);

export const clearRecordingDuplicates = (broadcastId?: string) =>
  useRecordingDuplicatesStore.getState().clearRecordings(broadcastId);

export const loadRecordingDuplicates = (broadcastId: string) =>
  useRecordingDuplicatesStore.getState().loadRecordings(broadcastId);

export const getOriginalRecording = (broadcastId: string): Recording | null =>
  useRecordingDuplicatesStore.getState().recordings[broadcastId]?.original ?? null;
